import React, { useEffect, useState, useMemo } from "react";
import {
  Plus,
  Search,
  ClipboardList,
  PenLine,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import AddQuestionComponent from "../../components/add_question.js";
import QuestionForm from "./QuestionForm.js";
import axios from "../../routers/axios"; // Sử dụng axios từ cấu hình chung
import { showDeleteAlert, showSuccessAlert, showErrorAlert } from "../../utils/deleteAlert";

const PAGE_SIZE = 10;

const QUESTION_TYPES = [
  { key: "all", label: "Tất cả" },
  { key: "multiple_choice", label: "Multiple choice" },
  { key: "true_false", label: "True / False" },
  { key: "true_false_not_given", label: "True / False / Not given" },
  { key: "classification", label: "Classification" },
  { key: "job_comparison", label: "Job comparison" },
  { key: "matching", label: "Matching" },
  { key: "synthesis_matrix", label: "Synthesis matrix" },
  { key: "short_answer", label: "Short answer" },
];

const BADGE_STYLES = {
  multiple_choice: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
  true_false: "bg-green-50 text-green-700 ring-1 ring-green-200",
  true_false_not_given: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  classification: "bg-teal-50 text-teal-700 ring-1 ring-teal-200",
  job_comparison: "bg-violet-50 text-violet-700 ring-1 ring-violet-200",
  matching: "bg-orange-50 text-orange-700 ring-1 ring-orange-200",
  synthesis_matrix: "bg-pink-50 text-pink-700 ring-1 ring-pink-200",
  short_answer: "bg-gray-100 text-gray-600 ring-1 ring-gray-200",
};

export default function QuestionList() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // ID câu hỏi đang sửa
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/questions/list");
      const data = res.data;
      const list = Array.isArray(data) ? data : data?.items ?? [];
      setQuestions(list);
    } catch (err) {
      console.error("❌ Lỗi khi lấy câu hỏi:", err);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleDelete = (question) => {
    showDeleteAlert({
      title: "Xóa câu hỏi?",
      text: `Bạn có chắc chắn muốn xóa câu hỏi có ID: ${question.id}? Hành động này không thể hoàn tác.`,
      confirmButtonText: "Xác nhận xóa",
      onConfirm: async () => {
        try {
          const res = await axios.delete(`/questions/list/${question.id}`);
          if (res.status === 200 || res.status === 204) {
            showSuccessAlert("Câu hỏi đã được xóa thành công.");
            fetchQuestions();
          } else {
            showErrorAlert("Không thể xóa câu hỏi. Vui lòng kiểm tra lại.");
          }
        } catch (err) {
          console.error("❌ Lỗi khi xoá:", err);
          showErrorAlert("Đã xảy ra lỗi khi xóa câu hỏi. Vui lòng thử lại.");
        }
      },
    });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
    setSearchQuery("");
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const filtered = useMemo(() => {
    let list =
      activeTab === "all"
        ? questions
        : questions.filter((q) => q.question_type === activeTab);

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (item) =>
          item.question_text?.toLowerCase().includes(q) ||
          String(item.id).includes(q)
      );
    }

    return [...list].sort((a, b) => a.id - b.id);
  }, [questions, activeTab, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const renderPageButtons = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (
        totalPages > 7 &&
        i > 2 &&
        i < totalPages - 1 &&
        Math.abs(i - currentPage) > 1
      ) {
        if (i === 3 || i === totalPages - 2)
          pages.push(
            <span key={`ellipsis-${i}`} className="px-1 text-gray-400 select-none">
              …
            </span>
          );
        continue;
      }
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
            i === currentPage
              ? "bg-blue-600 text-white"
              : "border border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <ClipboardList size={20} className="text-gray-600" />
          <h1 className="text-xl font-semibold text-gray-800">Quản lý câu hỏi</h1>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-[.98] text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-all"
        >
          <Plus size={15} />
          Thêm câu hỏi
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-gray-200 mb-4 overflow-x-auto">
        {QUESTION_TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === t.key
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Tìm theo nội dung hoặc ID..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition"
          />
        </div>
        <span className="ml-auto text-xs text-gray-400">{filtered.length} câu hỏi</span>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-sm text-gray-400">Đang tải dữ liệu…</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 w-14">ID</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 w-44">Loại</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500">Nội dung</th>
                <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 w-16">Điểm</th>
                <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 w-32">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length > 0 ? (
                paginated.map((q, idx) => (
                  <tr
                    key={q.id}
                    className={`border-t border-gray-100 transition-colors hover:bg-gray-50 ${
                      idx % 2 === 1 ? "bg-gray-50/40" : ""
                    }`}
                  >
                    <td className="py-3 px-4 text-sm tabular-nums text-gray-400 font-mono">{q.id}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          BADGE_STYLES[q.question_type] ?? BADGE_STYLES.short_answer
                        }`}
                      >
                        {q.question_type?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700 max-w-xs truncate">
                      {q.question_text
                        ? q.question_text.length > 80
                          ? q.question_text.slice(0, 80) + "…"
                          : q.question_text
                        : "—"}
                    </td>
                    <td className="py-3 px-4 text-sm text-center text-gray-700">{q.points}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setEditingId(q.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
                        >
                          <PenLine size={12} />
                          Sửa
                        </button>
                        <button
                          onClick={() => handleDelete(q)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                        >
                          <Trash2 size={12} />
                          Xóa
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-16 text-center text-sm text-gray-400 italic">
                    Không tìm thấy câu hỏi nào
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-end gap-1.5 mt-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:pointer-events-none transition-colors"
          >
            <ChevronLeft size={14} />
          </button>
          {renderPageButtons()}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:pointer-events-none transition-colors"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Modal Thêm câu hỏi - Gọi trực tiếp AddQuestionComponent, ko bọc thêm div modal nữa */}
      {showAddModal && (
        <AddQuestionComponent
          onClose={() => {
            setShowAddModal(false);
            fetchQuestions();
          }}
        />
      )}

      {/* Modal Sửa câu hỏi - Gọi trực tiếp QuestionForm, ko bọc thêm div modal nữa */}
      {editingId && (
        <QuestionForm
          id={editingId}
          onClose={() => {
            setEditingId(null);
            fetchQuestions();
          }}
        />
      )}
    </div>
  );
}
